import * as Api from '@ViewModels';
import {
	addEdge,
	applyEdgeChanges,
	applyNodeChanges,
	Background,
	type Connection,
	Controls,
	type Edge,
	type EdgeChange,
	MiniMap,
	type Node,
	type NodeChange,
	type NodeMouseHandler,
	type OnConnect,
	type OnSelectionChangeFunc,
	Panel,
	ReactFlow,
	type ReactFlowInstance,
	useReactFlow,
	useViewport,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { css } from 'aphrodite';
import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import { toast } from 'sonner';
import { AutomationFlowProvider } from '../contexts/AutomationFlowContext';
import { edgeTypes } from '../edges';
import { useAutomationConnectionTypes } from '../hooks/useAutomationConnectionTypes';
import { useCanvasDragDrop } from '../hooks/useCanvasDragDrop';
import { useCanvasKeyboardShortcuts } from '../hooks/useCanvasKeyboardShortcuts';
import { useCanvasMode } from '../hooks/useCanvasMode';
import { useChainStepAddition } from '../hooks/useChainStepAddition';
import { type ChainTerminus, useChainTermini } from '../hooks/useChainTermini';
import { useContextMenu } from '../hooks/useContextMenu';
import { useContextMenuActions } from '../hooks/useContextMenuActions';
import { useCopyPaste } from '../hooks/useCopyPaste';
import { useDeleteConfirmation } from '../hooks/useDeleteConfirmation';
import { useNodeSearch } from '../hooks/useNodeSearch';
import { useReordering } from '../hooks/useReordering';
import { useReorderSegmentsModal } from '../hooks/useReorderSegmentsModal';
import { useSelectionGroup } from '../hooks/useSelectionGroup';
import { useUndoRedo } from '../hooks/useUndoRedo';
import { nodeTypes } from '../nodes';
import { type AutomationStepNodeData, type BaseNodeData, NODE_TYPES } from '../types';
import type { AutomationFlowCanvasProps } from '../types/canvasProps';
import type { FlowContact } from '../types/contacts';
import type { SegmentNodeData } from '../types/stepNodes';
import { applyStepValidityToNodes } from '../utils/applyStepValidity';
import { getTagStepDisplayName, validateStep } from '../utils/convertTemplateToFlow';
import { findFlowNodeIdByStepId, getPathEdgeIds, getPathNodeIdsToRoot } from '../utils/flowPathing';
import { mergeIncomingNodeLayout, shouldDeferIncomingBranchGraphSync } from '../utils/incomingBranchGraphSync';
import { applyLayout } from '../utils/layoutEngine';
import { wouldCreateCycle } from '../utils/layoutHelpers';
import { canMoveStepDown, canMoveStepUp } from '../utils/nodeReordering';
import { resolveSwitchOwnerTemplateId } from '../utils/branchNodes';
import { applyLocalSegmentOrder, getOrderedSegments } from '../utils/segmentReordering';
import { areContactsEqualById } from '../utils/simulateContactFlow';
import { applyPersistedSwitchCaseGraphUpdate } from '../utils/switchCaseGraphSync';
import { getStepTypeBorderColor, TRIGGER_INDICATOR_COLOR } from '../utils/stepTypeColors';
import { canUseTriggerAutomationStep } from '../utils/triggerAutomationStepRules';
import { DEFAULT_CANVAS_FIT_VIEW_OPTIONS, scheduleCenterOnNode } from '../utils/viewport';
import { CanvasContextMenu } from './CanvasContextMenu';
import { CanvasControls } from './CanvasControls';
import { ChainTerminusButton } from './ChainTerminusButton';
import { DeleteStepConfirmationDialog } from './DeleteStepConfirmationDialog';
import { FloatingAddSegmentButton } from './FloatingAddSegmentButton';
import { KeyboardShortcutsHelp, useKeyboardShortcuts } from './KeyboardShortcutsHelp';
import { NodeSearchPanel } from './NodeSearchPanel';
import { ReorderSegmentsModal } from './ReorderSegmentsModal';
import { SelectionGroupOverlay } from './SelectionGroupOverlay';
import { styleSheet } from './styles/AutomationFlowCanvasStyles';

function isContextMenuEventOutsideCanvas(event: ReactMouseEvent, containerElement: HTMLDivElement | null): boolean {
	if (!containerElement) {
		return false;
	}

	const composedPath = event.nativeEvent.composedPath?.();
	if (composedPath) {
		return !composedPath.includes(containerElement);
	}

	const eventTarget = event.target;
	if (!eventTarget || typeof eventTarget !== 'object' || !('nodeType' in eventTarget)) {
		return true;
	}

	try {
		return !containerElement.contains(eventTarget as globalThis.Node);
	} catch {
		// Cross-document nodes (for example editor iframes) may fail `contains`; treat them as outside
		// the canvas so portal events cannot reopen the node context menu.
		return true;
	}
}

const LAYOUT_DIMENSION_EPSILON_PX = 1;
// When a node is first mounted, React Flow reports its measured dimensions.
// If we've already provided a decent initial width/height estimate on the node,
// allow a bit more tolerance so we don't trigger a pointless relayout burst.
const INITIAL_DIMENSION_EPSILON_PX = 8;
const LAYOUT_POSITION_EPSILON_PX = 0.5;
const MAX_LAYOUT_PASSES_PER_BURST = 3;

function buildAutoLayoutKey(nodes: Node<BaseNodeData>[], edges: Edge[]): string {
	const nodeKey = nodes
		.map(n => {
			const measuredWidth = typeof n.measured?.width === 'number' ? n.measured.width : undefined;
			const measuredHeight = typeof n.measured?.height === 'number' ? n.measured.height : undefined;
			const width = Math.round(
				typeof measuredWidth === 'number' ? measuredWidth : typeof n.width === 'number' ? n.width : 0
			);
			const height = Math.round(
				typeof measuredHeight === 'number' ? measuredHeight : typeof n.height === 'number' ? n.height : 0
			);
			const estimatedHeightValue = (n.data as Record<string, unknown>).estimatedHeight;
			const estimatedHeight = typeof estimatedHeightValue === 'number' ? Math.round(estimatedHeightValue) : 0;
			const isExpanded = (n.data as { isExpanded?: unknown }).isExpanded === true ? 1 : 0;
			// Include whether dimensions have been measured. Nodes that still
			// have width/height 0 (unmeasured) must not dedup against the same
			// node once React Flow measures it, because the layout needs to
			// rerun with the correct dimensions.
			const hasMeasured = typeof measuredWidth === 'number' && typeof measuredHeight === 'number' ? 1 : 0;
			return `${n.id}:${n.type ?? ''}:${width}x${height}:est${estimatedHeight}:exp${isExpanded}:m${hasMeasured}`;
		})
		.sort()
		.join('|');

	const edgeKey = edges
		.map(e => {
			const caseIndex =
				typeof (e.data as { caseIndex?: unknown } | undefined)?.caseIndex === 'number'
					? (e.data as { caseIndex: number }).caseIndex
					: '';
			return `${e.source}->${e.target}:${e.sourceHandle ?? ''}:${e.targetHandle ?? ''}:${e.type ?? ''}:${caseIndex}`;
		})
		.sort()
		.join('|');

	// Include structural counts so that adding/removing a node always
	// produces a distinct key, even when the new node has zero dimensions.
	return `n${nodes.length}:e${edges.length}__${nodeKey}__${edgeKey}`;
}

function didLayoutMoveNodes(before: Node<BaseNodeData>[], after: Node<BaseNodeData>[]): boolean {
	if (before.length !== after.length) {
		return true;
	}
	const beforeMap = new Map(before.map(n => [n.id, n] as const));
	for (const node of after) {
		const prev = beforeMap.get(node.id);
		if (!prev) {
			return true;
		}
		if (
			Math.abs(prev.position.x - node.position.x) > LAYOUT_POSITION_EPSILON_PX ||
			Math.abs(prev.position.y - node.position.y) > LAYOUT_POSITION_EPSILON_PX
		) {
			return true;
		}
	}
	return false;
}

function isBranchEntryNode(node: Node<BaseNodeData>): boolean {
	return node.type === NODE_TYPES.SEGMENT || node.type === NODE_TYPES.BEHAVIOR_CASE;
}

function normalizeBranchEntryNodeInteractivity(node: Node<BaseNodeData>): Node<BaseNodeData> {
	if (!isBranchEntryNode(node)) {
		return node;
	}

	// Behavior case nodes are fully locked: no drag, no select. When both are
	// false React Flow sets pointer-events:none, which also suppresses the
	// context menu — intentional, because behaviors should not be deleted.
	if (node.type === NODE_TYPES.BEHAVIOR_CASE) {
		if (node.draggable === false && node.selectable === false && node.selected !== true) {
			return node;
		}
		return {
			...node,
			draggable: false,
			selectable: false,
			selected: false,
		};
	}

	// Segment nodes: prevent dragging but keep selectable so React Flow
	// preserves pointer-events and fires onNodeContextMenu on right-click.
	// Explicitly clear selectable/selected in case an earlier normalization
	// pass set them to false (the value persists on the node object).
	if (node.draggable === false && node.selectable !== false && node.selected !== true) {
		return node;
	}
	return {
		...node,
		draggable: false,
		selectable: undefined,
		selected: node.selected === true ? false : node.selected,
	};
}

export function AutomationFlowCanvas({
	mode = 'editing',
	initialNodes = [],
	initialEdges = [],
	syncVersion,
	onNodesChange: onNodesChangeProp,
	onEdgesChange: onEdgesChangeProp,
	onSelectionChange: onSelectionChangeProp,
	activeContacts = [],
	selectedSwitchNodeId,
	templateId,
	automationTemplate,
	impersonationContext,
	onAddSegment,
	onAddStepToChain,
	onDeleteSteps,
	onDeleteBranchCases,
	onDuplicateStep,
	onPasteSteps,
	onReorderSteps,
	onReorderSegments,
	onMoveStepsCrossSegment,
	onUpdateCaseCriteria,
	onUpdateCaseStageIds,
	onUpdateCaseName,
	showChainTerminusButtons,
	excludedStepTypes,
	validationResult,
	invalidStepIds,
	highlightedValidationIssue,
}: AutomationFlowCanvasProps): React.ReactElement {
	const modeConfig = useCanvasMode(mode);
	const shouldShowChainTerminusButtons = showChainTerminusButtons ?? modeConfig.showChainTerminusButtons;

	// Undo/redo state management
	const { nodes, edges, setNodes, setEdges, canUndo, canRedo, undo, redo, takeSnapshot, resetHistory } = useUndoRedo({
		initialNodes,
		initialEdges,
	});

	// React Flow instance methods
	const { screenToFlowPosition, fitView, setCenter, zoomIn, zoomOut, getNodes: getReactFlowNodes } = useReactFlow();

	const rootTrigger = automationTemplate?.draft?.trigger ?? automationTemplate?.published?.trigger;
	const effectiveExcludedStepTypes = useMemo(() => {
		const stepTypes = new Set(excludedStepTypes || []);
		if (!canUseTriggerAutomationStep(rootTrigger)) {
			stepTypes.add(NODE_TYPES.TRIGGER_AUTOMATION_STEP);
		}
		return Array.from(stepTypes);
	}, [excludedStepTypes, rootTrigger]);

	// Copy/paste functionality
	const { hasClipboard, copySelection, pasteAtPosition } = useCopyPaste({ nodes, edges });

	// Unified reordering (single-node + group drag)
	const {
		selectBranchNodes,
		moveUp,
		moveDown,
		groupDragState,
		startGroupDrag,
		updateGroupDrag,
		endGroupDrag,
		cancelGroupDrag,
	} = useReordering({
		nodes,
		edges,
		setNodes,
		setEdges,
		takeSnapshot,
		onReorderComplete: onReorderSteps,
		onMoveStepsCrossSegment,
	});

	// Selection group functionality
	const { buildSelectionGroup } = useSelectionGroup({ nodes, edges });

	// Chain termini for floating + buttons
	const chainTermini = useChainTermini(nodes, edges);

	// Context menu state and actions
	const {
		state: contextMenuState,
		openMenu: openContextMenu,
		openMenuAtPosition: openContextMenuAtPosition,
		closeMenu: closeContextMenu,
	} = useContextMenu();
	// Confirmation gate shared by the keyboard (Delete/Backspace) and context-menu delete paths.
	const { pendingDelete, requestDeleteNodes, confirmDelete, cancelDelete } = useDeleteConfirmation({
		nodes,
		edges,
		setNodes,
		setEdges,
		takeSnapshot,
		onDeleteSteps,
		onDeleteBranchCases,
	});
	const { handleDelete, handleDuplicate, handleMoveUp, handleMoveDown } = useContextMenuActions({
		nodes,
		edges,
		setNodes,
		setEdges,
		takeSnapshot,
		copySelection,
		pasteAtPosition,
		moveUp,
		moveDown,
		onRequestDeleteNodes: requestDeleteNodes,
		onDuplicateStep,
	});

	// Reorder segments modal state
	const {
		state: reorderSegmentsModalState,
		openModal: openReorderSegmentsModal,
		closeModal: closeReorderSegmentsModal,
	} = useReorderSegmentsModal();

	// Node search functionality
	const {
		searchState,
		openSearch,
		closeSearch,
		updateQuery: updateSearchQuery,
		navigateToNode,
	} = useNodeSearch({ nodes, setNodes, setCenter });

	// Local UI state
	const [animateEdges, setAnimateEdges] = useState(false);
	const [autoConnect, setAutoConnect] = useState(true);
	const [isMiniMapVisible, setIsMiniMapVisible] = useState(true);
	const { isHelpOpen, openHelp, closeHelp } = useKeyboardShortcuts();
	const lastSyncedIdsRef = useRef<string>('');
	const lastSyncedVersionRef = useRef<number>(0);
	const pendingLayoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	// Guard to prevent re-entrant layout: when applyLayout runs and changes
	// node positions, React Flow fires onNodesChange with dimension changes
	// for the repositioned nodes, which would trigger another applyLayout.
	const isLayoutInProgressRef = useRef(false);
	// When dimension changes arrive while the guard is active, we must not
	// discard them entirely — the measurements may reflect a real content
	// change (e.g. a node collapsing). This flag queues a re-layout for
	// after the guard releases.
	const needsRelayoutRef = useRef(false);

	// Dedup + safety against runaway layout loops.
	const lastAutoLayoutKeyRef = useRef<string>('');
	const pendingAutoLayoutKeyRef = useRef<string>('');
	const layoutPassCountRef = useRef<number>(0);
	const lastLayoutRunAtRef = useRef<number>(0);

	// React Flow requires a non-zero parent size at mount time. In some app layouts,
	// the canvas can briefly mount at 0px height while flex/grid finishes resolving.
	// Gate rendering until we have a measurable box to prevent React Flow error #004.
	const containerRef = useRef<HTMLDivElement>(null);
	const [hasContainerSize, setHasContainerSize] = useState(false);
	const [isFlowInitialized, setIsFlowInitialized] = useState(false);
	useEffect(() => {
		const el = containerRef.current;
		if (!el) {
			return;
		}

		const update = () => {
			const rect = el.getBoundingClientRect();
			// Once we've observed a non-zero box, keep it true.
			// Flipping back to false would unmount/remount ReactFlow, which resets viewport
			// and looks like the canvas is "jumping".
			setHasContainerSize(prev => prev || (rect.width > 0 && rect.height > 0));
		};

		update();
		const resizeObserver = new ResizeObserver(() => update());
		resizeObserver.observe(el);
		return () => resizeObserver.disconnect();
	}, []);

	const isDebugCanvas =
		typeof window !== 'undefined' &&
		(typeof window.location?.search === 'string' ? window.location.search.includes('debugCanvas=1') : false);
	const useContainerLayoutV2 =
		typeof window === 'undefined' ||
		typeof window.location?.search !== 'string' ||
		!window.location.search.includes('useContainerLayoutV2=0');
	const { connectionTypes } = useAutomationConnectionTypes(impersonationContext);

	const toggleMiniMap = useCallback(() => {
		setIsMiniMapVisible(prev => !prev);
	}, []);

	// Keep latest nodes/edges in refs for async work (layout debounce, raf-throttled parent sync)
	const latestNodesRef = useRef(nodes);
	const latestEdgesRef = useRef(edges);
	useEffect(() => {
		latestNodesRef.current = nodes;
	}, [nodes]);
	useEffect(() => {
		latestEdgesRef.current = edges;
	}, [edges]);

	const lastCenteredValidationIssueKeyRef = useRef<string>('');

	const queueNodeDataAutoLayout = useCallback(
		(debounceMs = 0) => {
			if (Date.now() - lastLayoutRunAtRef.current > 400) {
				layoutPassCountRef.current = 0;
			}

			if (isLayoutInProgressRef.current) {
				needsRelayoutRef.current = true;
				return;
			}

			if (pendingLayoutRef.current) {
				return;
			}

			pendingLayoutRef.current = setTimeout(() => {
				pendingLayoutRef.current = null;

				if (
					layoutPassCountRef.current >= MAX_LAYOUT_PASSES_PER_BURST &&
					Date.now() - lastLayoutRunAtRef.current < 1200
				) {
					return;
				}

				isLayoutInProgressRef.current = true;
				needsRelayoutRef.current = false;
				pendingAutoLayoutKeyRef.current = '';

				setNodes(currentNodes => {
					const key = buildAutoLayoutKey(currentNodes, latestEdgesRef.current);
					lastAutoLayoutKeyRef.current = key;

					const layoutResult = applyLayout(currentNodes, latestEdgesRef.current, { useContainerLayoutV2 });
					const moved = didLayoutMoveNodes(currentNodes, layoutResult.nodes);
					lastLayoutRunAtRef.current = Date.now();
					layoutPassCountRef.current += 1;
					return moved ? layoutResult.nodes : currentNodes;
				});

				requestAnimationFrame(() => {
					requestAnimationFrame(() => {
						isLayoutInProgressRef.current = false;

						if (needsRelayoutRef.current) {
							needsRelayoutRef.current = false;
							queueNodeDataAutoLayout(50);
						}
					});
				});
			}, debounceMs);
		},
		[setNodes, useContainerLayoutV2]
	);

	const handleRunAutoLayout = useCallback(() => {
		// Cancel any pending debounced layout pass.
		if (pendingLayoutRef.current) {
			clearTimeout(pendingLayoutRef.current);
			pendingLayoutRef.current = null;
		}

		// New burst: clear dedupe + pass counter.
		pendingAutoLayoutKeyRef.current = '';
		layoutPassCountRef.current = 0;

		isLayoutInProgressRef.current = true;
		needsRelayoutRef.current = false;

		const layoutResult = applyLayout(latestNodesRef.current, latestEdgesRef.current, { useContainerLayoutV2 });
		lastAutoLayoutKeyRef.current = buildAutoLayoutKey(layoutResult.nodes, layoutResult.edges);
		lastLayoutRunAtRef.current = Date.now();
		layoutPassCountRef.current += 1;
		setNodes(layoutResult.nodes);
		setEdges(layoutResult.edges);

		// Release the guard after React Flow processes the layout.
		// Use double-RAF so both the React commit and React Flow's internal
		// measure pass complete before we accept new dimension changes.
		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				isLayoutInProgressRef.current = false;
				// If dimension changes arrived while the guard was active,
				// run a follow-up layout with the now-accurate measurements.
				if (needsRelayoutRef.current) {
					needsRelayoutRef.current = false;

					// Safety: if we keep receiving dimension changes every frame (e.g. height
					// animation / ResizeObserver churn), don't loop forever.
					if (layoutPassCountRef.current >= MAX_LAYOUT_PASSES_PER_BURST) {
						return;
					}

					pendingLayoutRef.current = setTimeout(() => {
						isLayoutInProgressRef.current = true;
						pendingAutoLayoutKeyRef.current = '';
						setNodes(currentNodes => {
							const key = buildAutoLayoutKey(currentNodes, latestEdgesRef.current);
							lastAutoLayoutKeyRef.current = key;

							const layoutResult2 = applyLayout(currentNodes, latestEdgesRef.current, {
								useContainerLayoutV2,
							});
							lastLayoutRunAtRef.current = Date.now();
							layoutPassCountRef.current += 1;
							return didLayoutMoveNodes(currentNodes, layoutResult2.nodes) ? layoutResult2.nodes : currentNodes;
						});
						requestAnimationFrame(() => {
							requestAnimationFrame(() => {
								isLayoutInProgressRef.current = false;
							});
						});
						pendingLayoutRef.current = null;
					}, 50);
				}
			});
		});

		// After relayout, fit the viewport so the user can see the whole graph.
		if (hasContainerSize) {
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					fitView(DEFAULT_CANVAS_FIT_VIEW_OPTIONS);
				});
			});
		}
	}, [fitView, hasContainerSize, setNodes, setEdges, useContainerLayoutV2]);

	// Track whether the viewport needs an initial fit (first load or template switch).
	// Seeded from the initial payload so a first mount with already-populated nodes
	// auto-fits. Prefer fitting after layout completes, with a post-mount fallback
	// for cases where no layout pass is needed.
	const needsInitialFitRef = useRef(initialNodes.length > 0);

	const handleCanvasInit = useCallback((_instance: ReactFlowInstance<Node<BaseNodeData>>) => {
		setIsFlowInitialized(true);
		needsInitialFitRef.current = true;
	}, []);

	// If the initial render doesn't trigger a layout pass (for example when the
	// measured node sizes are close to their initial estimates), the layout-driven
	// fit path below never runs and the canvas can stay anchored top-left.
	// Fall back to the same full-canvas fit used by the Fit View control once the
	// flow has mounted and the first paint has settled.
	useEffect(() => {
		if (!isFlowInitialized || !hasContainerSize || !needsInitialFitRef.current || nodes.length === 0) {
			return;
		}
		if (pendingLayoutRef.current || isLayoutInProgressRef.current) {
			return;
		}

		let raf1: number | null = null;
		let raf2: number | null = null;

		raf1 = requestAnimationFrame(() => {
			raf2 = requestAnimationFrame(() => {
				if (!needsInitialFitRef.current || pendingLayoutRef.current || isLayoutInProgressRef.current) {
					return;
				}

				needsInitialFitRef.current = false;
				fitView(DEFAULT_CANVAS_FIT_VIEW_OPTIONS);
			});
		});

		return () => {
			if (raf1 !== null) {
				cancelAnimationFrame(raf1);
			}
			if (raf2 !== null) {
				cancelAnimationFrame(raf2);
			}
		};
	}, [fitView, hasContainerSize, isFlowInitialized, nodes]);

	// Throttle syncing nodes/edges back to the parent to once-per-animation-frame.
	// During drag/resize React Flow can produce very frequent updates; without throttling
	// we end up re-rendering the entire editor tree at mousemove frequency.
	// A single RAF handles both nodes and edges to avoid races where the parent
	// receives new nodes but stale edges (or vice versa).
	const pendingParentSyncRafRef = useRef<number | null>(null);

	// Drag-drop functionality
	const { closestTerminusId, onDragOver, onDragLeave, onDrop } = useCanvasDragDrop({
		nodes,
		edges,
		setNodes,
		setEdges,
		chainTermini,
		autoConnect,
		animateEdges,
		activeContacts,
		screenToFlowPosition,
		setCenter,
		takeSnapshot,
		onAddStepToChain,
		automationTemplate,
		excludedStepTypes: effectiveExcludedStepTypes,
	});

	// Chain step addition
	const handleAddStepToChain = useChainStepAddition({
		nodes,
		edges,
		setNodes,
		setEdges,
		animateEdges,
		activeContacts,
		setCenter,
		takeSnapshot,
		onAddStepToChain,
		automationTemplate,
		excludedStepTypes: effectiveExcludedStepTypes,
	});

	// Callback for updating node data from child components
	const updateNodeData = useCallback(
		(nodeId: string, newData: Record<string, unknown>) => {
			const layoutAffectsGeometry =
				Object.prototype.hasOwnProperty.call(newData, 'isExpanded') ||
				Object.prototype.hasOwnProperty.call(newData, 'estimatedHeight') ||
				Object.prototype.hasOwnProperty.call(newData, 'isCompact');

			setNodes(nds => {
				const updatedNodes = nds.map(node => {
					if (node.id !== nodeId) {
						return node;
					}

					const merged = { ...node.data, ...newData };
					const existingStepData = ((node.data as AutomationStepNodeData).stepData || {}) as Record<string, unknown>;
					const incomingStepData = ((newData as { stepData?: Record<string, unknown> }).stepData || {}) as Record<
						string,
						unknown
					>;

					// Keep stepData in sync with automationStep updates so node titles/summaries update immediately.
					if ('automationStep' in newData && newData.automationStep) {
						const automationStepOrFn = newData.automationStep;
						let updatedStep: Api.IAutomationStep;
						if (typeof automationStepOrFn === 'function') {
							const patchFn = automationStepOrFn as (current: Api.IAutomationStep) => Partial<Api.IAutomationStep>;
							const currentStep = (node.data as AutomationStepNodeData).automationStep as Api.IAutomationStep;
							updatedStep = { ...currentStep, ...patchFn(currentStep) } as Api.IAutomationStep;
							merged.automationStep = updatedStep;
						} else {
							updatedStep = automationStepOrFn as Api.IAutomationStep;
						}
						merged.isValid = validateStep(updatedStep);

						const mergedStepData: Record<string, unknown> = {
							...existingStepData,
							...incomingStepData,
						};

						// Tag steps: derive display name from tagName.
						const tagDisplayName = getTagStepDisplayName(updatedStep);
						if (tagDisplayName !== null) {
							mergedStepData.name = tagDisplayName;
							merged.label = tagDisplayName;
						} else if (updatedStep._type === 'ActionItemAutomationStep') {
							// Action items derive their display name from rich-text content, not step.name.
							// The renderer (ActionItemStepRenderer) already computes and sends the correct
							// stepData.name and label via updateNodeData — trust those values.
						} else if (typeof updatedStep.name === 'string') {
							mergedStepData.name = updatedStep.name;
							if (updatedStep.name.trim().length > 0) {
								merged.label = updatedStep.name;
							}
						}

						if ('schedule' in updatedStep) {
							mergedStepData.schedule = updatedStep.schedule;
						}

						merged.stepData = mergedStepData;
					} else if (Object.keys(incomingStepData).length > 0) {
						merged.stepData = {
							...existingStepData,
							...incomingStepData,
						};
					}

					return { ...node, data: merged };
				});

				return updatedNodes;
			});

			if (layoutAffectsGeometry) {
				queueNodeDataAutoLayout(Object.prototype.hasOwnProperty.call(newData, 'isExpanded') ? 0 : 50);
			}
		},
		[queueNodeDataAutoLayout, setNodes]
	);

	// Keyboard shortcuts
	useCanvasKeyboardShortcuts({
		nodes,
		edges,
		setNodes,
		setEdges,
		modeConfig: {
			isReadOnly: modeConfig.isReadOnly,
			elementsSelectable: modeConfig.elementsSelectable,
		},
		undoRedo: { canUndo, canRedo, undo, redo },
		copyPaste: { hasClipboard, copySelection, pasteAtPosition },
		takeSnapshot,
		screenToFlowPosition,
		fitView,
		zoomIn,
		zoomOut,
		getReactFlowNodes: getReactFlowNodes as () => Node<BaseNodeData>[],
		selectBranchNodes,
		groupDragState: { isDragging: groupDragState.isDragging },
		cancelGroupDrag,
		chainTermini,
		openSearch: modeConfig.isReadOnly ? undefined : openSearch,
		excludedStepTypes: effectiveExcludedStepTypes,
		onRequestDeleteNodes: requestDeleteNodes,
		onDuplicateStep,
		onPasteSteps,
	});

	// Sync initialNodes/initialEdges when a completely different template is loaded,
	// or when syncVersion is bumped (structural change from server, e.g., new segment added).
	useEffect(() => {
		if (initialNodes.length === 0) {
			return;
		}
		const incomingIds = new Set(initialNodes.map((n: Node<BaseNodeData>) => n.id));
		const incomingIdsKey = [...incomingIds].sort().join(',');

		// Force-sync when the parent bumps syncVersion (structural server change).
		// This bypasses the overlap check because adding a segment produces high
		// overlap but the canvas must still accept the new nodes.
		const isForceSync = syncVersion !== undefined && syncVersion > lastSyncedVersionRef.current;
		if (isForceSync) {
			resetHistory(initialNodes, initialEdges);
			// Do NOT fitView here.
			// `syncVersion` is used to force the canvas to accept a new graph snapshot,
			// and auto-fitting causes the viewport to jump unexpectedly.
			lastSyncedIdsRef.current = incomingIdsKey;
			lastSyncedVersionRef.current = syncVersion;

			// Clear dedup key so the next dimension-triggered layout pass runs
			// Dagre even if the node sizes haven't changed. Structural changes
			// (new/removed nodes, re-ordered edges) require a fresh layout.
			lastAutoLayoutKeyRef.current = '';
			pendingAutoLayoutKeyRef.current = '';
			layoutPassCountRef.current = 0;
			return;
		}

		// Skip if we've already synced this exact set of IDs
		if (incomingIdsKey === lastSyncedIdsRef.current) {
			return;
		}

		// Compare incoming IDs to the canvas's current IDs.
		const currentNodes = latestNodesRef.current;
		const currentIds = new Set(currentNodes.map(n => n.id));
		const hasNewIds = [...incomingIds].some(id => !currentIds.has(id));
		const hasMissingIds = [...currentIds].some(id => !incomingIds.has(id));

		if (hasMissingIds && !hasNewIds) {
			// The canvas has nodes the parent doesn't (yet). This is expected after
			// optimistic updates (e.g. applyPersistedSwitchCaseGraphUpdate adds
			// segment/phantom nodes before the refetch catches up).
			//
			// Don't resetHistory here — it would blow away the optimistic state,
			// and if the parent oscillates between stale (2 nodes) and fresh (6 nodes)
			// data, the alternating resets create an infinite re-render loop.
			//
			// Node removal is handled by the force-sync path (setCanvasSyncVersion),
			// which runs above and takes priority. Don't update lastSyncedIdsRef so
			// the dedup check at line 555 catches the next matching payload.
			return;
		}

		if (
			(hasNewIds || hasMissingIds) &&
			shouldDeferIncomingBranchGraphSync({ currentNodes, incomingNodes: initialNodes })
		) {
			// Branch-creation mutations can invalidate the root template multiple times
			// before the full server graph is available. Ignore those partial snapshots
			// so the canvas does not flicker back to an incomplete layout.
			return;
		}

		if (hasNewIds) {
			// First mount: we want an initial fit so the user sees the flow.
			// (On incremental structural updates we intentionally do NOT auto-fit.)
			if (currentNodes.length === 0) {
				needsInitialFitRef.current = true;
			}

			const mergedIncoming = mergeIncomingNodeLayout({
				currentNodes,
				incomingNodes: initialNodes,
			});

			if (mergedIncoming.matchedCount > 0) {
				resetHistory(mergedIncoming.nodes, initialEdges);
			} else {
				// Preserve the user's viewport by anchoring the incoming layout to the
				// current trigger (or first node) position.
				const currentAnchor = currentNodes.find(n => n.type === 'trigger') ?? currentNodes[0];
				const incomingAnchor = currentAnchor
					? (initialNodes.find(n => n.id === currentAnchor.id) ?? initialNodes.find(n => n.type === 'trigger'))
					: (initialNodes.find(n => n.type === 'trigger') ?? initialNodes[0]);

				if (currentAnchor?.position && incomingAnchor?.position) {
					const dx = currentAnchor.position.x - incomingAnchor.position.x;
					const dy = currentAnchor.position.y - incomingAnchor.position.y;
					const shiftedNodes = initialNodes.map(n => ({
						...n,
						position: {
							x: n.position.x + dx,
							y: n.position.y + dy,
						},
					}));
					resetHistory(shiftedNodes, initialEdges);
				} else {
					resetHistory(initialNodes, initialEdges);
				}
			}

			// Clear dedup key so the next layout pass runs fresh (structural change).
			lastAutoLayoutKeyRef.current = '';
			pendingAutoLayoutKeyRef.current = '';
			layoutPassCountRef.current = 0;
			lastSyncedIdsRef.current = incomingIdsKey;
			return;
		}

		// Check if this is a genuinely NEW template by seeing if most IDs are different
		// If we have synced before, check overlap with previous sync
		if (lastSyncedIdsRef.current) {
			const previousIds = new Set(lastSyncedIdsRef.current.split(','));
			const overlapCount = [...incomingIds].filter(id => previousIds.has(id)).length;
			const overlapRatio = overlapCount / Math.max(incomingIds.size, previousIds.size);

			// If more than 50% overlap, this is likely just our changes propagating back
			// Don't reset history for incremental changes
			if (overlapRatio > 0.5) {
				// Update the ref but don't reset history
				lastSyncedIdsRef.current = incomingIdsKey;
				return;
			}
		}

		// This appears to be a new template - reset history
		resetHistory(initialNodes, initialEdges);
		needsInitialFitRef.current = true;
		lastSyncedIdsRef.current = incomingIdsKey;
	}, [initialNodes, initialEdges, resetHistory, syncVersion]);

	// Apply validation state directly to canvas-internal nodes.
	// This bypasses the broken initialNodes sync path: when the parent updates
	// isValid on its nodes, the canvas overlap check rejects the change because
	// the node IDs haven't changed. Instead, we write isValid directly into
	// the canvas's own setNodes, without taking a snapshot (validation changes
	// are not undoable user actions).
	useEffect(() => {
		// When we haven't run publish validation yet, don't overwrite the node's
		// local validation state (e.g. "empty email content").
		if (!validationResult || !invalidStepIds) {
			return;
		}

		// Preserving array identity when nothing changed is load-bearing: a fresh
		// array here flows back to the parent via the parent-sync effect, which
		// re-triggers auto-validation, which re-runs this effect — an infinite
		// render loop.
		setNodes(currentNodes => applyStepValidityToNodes(currentNodes, invalidStepIds));
	}, [validationResult, invalidStepIds, setNodes]);

	useEffect(() => {
		const targetNodeId = highlightedValidationIssue?.nodeId
			? highlightedValidationIssue.nodeId
			: highlightedValidationIssue?.stepId
				? findFlowNodeIdByStepId(latestNodesRef.current, highlightedValidationIssue.stepId)
				: null;

		const normalizedPathNodeIds = (() => {
			if (highlightedValidationIssue?.pathNodeIds?.length) {
				const nodeIdSet = new Set(latestNodesRef.current.map(node => node.id));
				const filteredPath = highlightedValidationIssue.pathNodeIds.filter(nodeId => nodeIdSet.has(nodeId));
				if (filteredPath.length > 0) {
					return filteredPath;
				}
			}

			if (!targetNodeId) {
				return [];
			}

			return getPathNodeIdsToRoot(targetNodeId, latestEdgesRef.current);
		})();

		const pathNodeIds =
			targetNodeId && !normalizedPathNodeIds.includes(targetNodeId)
				? [...normalizedPathNodeIds, targetNodeId]
				: normalizedPathNodeIds;
		const pathNodeIdSet = new Set(pathNodeIds);
		const pathEdgeIdSet = new Set(getPathEdgeIds(pathNodeIds, latestEdgesRef.current));

		setNodes(currentNodes => {
			let didChange = false;
			const nextNodes = currentNodes.map(node => {
				const nextValidationPathState: BaseNodeData['validationPathState'] =
					node.id === targetNodeId ? 'target' : pathNodeIdSet.has(node.id) ? 'path' : undefined;
				const currentValidationPathState = (node.data as BaseNodeData).validationPathState;

				if (currentValidationPathState === nextValidationPathState) {
					return node;
				}

				didChange = true;
				return {
					...node,
					data: {
						...node.data,
						validationPathState: nextValidationPathState,
					},
				};
			});

			return didChange ? nextNodes : currentNodes;
		});

		setEdges(currentEdges => {
			let didChange = false;
			const nextEdges = currentEdges.map(edge => {
				const currentIsValidationPath =
					(edge.data as { isValidationPath?: unknown } | undefined)?.isValidationPath === true;
				const nextIsValidationPath = pathEdgeIdSet.has(edge.id);

				if (currentIsValidationPath === nextIsValidationPath) {
					return edge;
				}

				didChange = true;
				return {
					...edge,
					data: {
						...edge.data,
						isValidationPath: nextIsValidationPath,
					},
				};
			});

			return didChange ? nextEdges : currentEdges;
		});

		const validationIssueKey = highlightedValidationIssue
			? `${highlightedValidationIssue.nodeId || highlightedValidationIssue.stepId || 'issue'}:${
					highlightedValidationIssue.message
				}`
			: '';
		if (!validationIssueKey) {
			lastCenteredValidationIssueKeyRef.current = '';
		} else if (targetNodeId && lastCenteredValidationIssueKeyRef.current !== validationIssueKey) {
			const targetNode = latestNodesRef.current.find(node => node.id === targetNodeId);
			if (targetNode) {
				lastCenteredValidationIssueKeyRef.current = validationIssueKey;
				scheduleCenterOnNode(setCenter, targetNode, { duration: 350, zoom: 1 });
			}
		}
	}, [highlightedValidationIssue, nodes, edges, setCenter, setEdges, setNodes]);

	// Cleanup pending async work on unmount
	useEffect(() => {
		return () => {
			if (pendingLayoutRef.current) {
				clearTimeout(pendingLayoutRef.current);
			}
			if (pendingParentSyncRafRef.current !== null) {
				cancelAnimationFrame(pendingParentSyncRafRef.current);
			}
		};
	}, []);

	// Node change handler - reflows on dimension changes with debouncing.
	// Uses isLayoutInProgressRef to prevent the cascade:
	//   dimension change → applyLayout → position change → dimension change → applyLayout …
	const onNodesChange = useCallback(
		(changes: NodeChange<Node<BaseNodeData>>[]) => {
			// React Flow can emit a stream of `dimensions` updates while nodes mount or
			// during CSS transitions. Some of these updates are effectively noise
			// (sub-pixel rounding or repeated reports of the same size).
			//
			// If we treat every `dimensions` update as a reason to re-run Dagre,
			// we can end up in a layout → re-measure → layout loop that looks like
			// flicker / infinite re-rendering.
			const dimensionChanges = changes.filter(
				(
					change
				): change is NodeChange<Node<BaseNodeData>> & {
					type: 'dimensions';
					dimensions: { width: number; height: number };
				} =>
					change.type === 'dimensions' &&
					Boolean((change as { dimensions?: unknown }).dimensions) &&
					typeof (change as { dimensions?: { width?: unknown } }).dimensions?.width === 'number' &&
					typeof (change as { dimensions?: { height?: unknown } }).dimensions?.height === 'number'
			);

			setNodes(nds => {
				const updatedNodes = applyNodeChanges(changes, nds);

				if (dimensionChanges.length === 0) {
					return updatedNodes;
				}

				// Ignore tiny/no-op dimension changes (often caused by rounding).
				const EPSILON_PX = LAYOUT_DIMENSION_EPSILON_PX;
				const hasSignificantDimensionChange = dimensionChanges.some(change => {
					const prevNode = nds.find(n => n.id === change.id);
					const hadMeasuredDimensions =
						typeof prevNode?.measured?.width === 'number' && typeof prevNode?.measured?.height === 'number';

					const prevWidth = typeof prevNode?.measured?.width === 'number' ? prevNode.measured.width : prevNode?.width;
					const prevHeight =
						typeof prevNode?.measured?.height === 'number' ? prevNode.measured.height : prevNode?.height;

					// If we didn't have any previous dimension signal (measured OR estimated width/height), treat it as significant.
					if (typeof prevWidth !== 'number' || typeof prevHeight !== 'number') {
						return true;
					}

					const epsilon = hadMeasuredDimensions ? EPSILON_PX : INITIAL_DIMENSION_EPSILON_PX;

					return (
						Math.abs(prevWidth - change.dimensions.width) > epsilon ||
						Math.abs(prevHeight - change.dimensions.height) > epsilon
					);
				});

				if (!hasSignificantDimensionChange) {
					return updatedNodes;
				}

				// Dedup: if we've already laid out for this exact node-size snapshot,
				// don't schedule another Dagre pass.
				const nextLayoutKey = buildAutoLayoutKey(updatedNodes, latestEdgesRef.current);
				if (nextLayoutKey === lastAutoLayoutKeyRef.current || nextLayoutKey === pendingAutoLayoutKeyRef.current) {
					return updatedNodes;
				}
				pendingAutoLayoutKeyRef.current = nextLayoutKey;
				// Reset the pass counter if the previous burst has settled, OR if
				// a node's height changed by more than 50px. Large height changes
				// indicate real content loading (e.g. behavior options panel appearing
				// after an async query resolves) rather than ResizeObserver churn,
				// and must not be suppressed by the burst limiter.
				const hasLargeHeightChange = dimensionChanges.some(change => {
					const prevNode = nds.find(n => n.id === change.id);
					const prevHeight =
						typeof prevNode?.measured?.height === 'number' ? prevNode.measured.height : prevNode?.height;
					if (typeof prevHeight !== 'number') {
						return true;
					}
					return Math.abs(prevHeight - change.dimensions.height) > 50;
				});
				if (Date.now() - lastLayoutRunAtRef.current > 400 || hasLargeHeightChange) {
					layoutPassCountRef.current = 0;
				}

				// If we've already tried several layout passes very recently, stop trying.
				// This prevents infinite "thrash" when something continuously triggers ResizeObserver updates.
				if (
					layoutPassCountRef.current >= MAX_LAYOUT_PASSES_PER_BURST &&
					Date.now() - lastLayoutRunAtRef.current < 1200
				) {
					pendingAutoLayoutKeyRef.current = '';
					return updatedNodes;
				}

				// If a layout pass is in progress, don't schedule immediately but
				// mark that a follow-up layout is needed. The guard-release callback
				// will pick this up and run the layout with fresh measurements.
				if (isLayoutInProgressRef.current) {
					needsRelayoutRef.current = true;
					return updatedNodes;
				}

				// Schedule one layout pass per burst of dimension changes.
				// Do NOT keep clearing/restarting the timer: a steady stream of
				// ResizeObserver events can otherwise starve the layout forever,
				// which looks like an infinite flicker loop.
				if (pendingLayoutRef.current) {
					return updatedNodes;
				}

				pendingLayoutRef.current = setTimeout(() => {
					// Safety: never loop indefinitely.
					if (layoutPassCountRef.current >= MAX_LAYOUT_PASSES_PER_BURST) {
						pendingAutoLayoutKeyRef.current = '';
						pendingLayoutRef.current = null;
						return;
					}

					isLayoutInProgressRef.current = true;
					needsRelayoutRef.current = false;

					// Capture whether we need to fit the viewport after this layout
					// pass completes (initial load or template switch).
					const shouldFitAfterLayout = needsInitialFitRef.current && hasContainerSize;
					if (shouldFitAfterLayout) {
						needsInitialFitRef.current = false;
					}

					setNodes(currentNodes => {
						const key = buildAutoLayoutKey(currentNodes, latestEdgesRef.current);
						lastAutoLayoutKeyRef.current = key;
						pendingAutoLayoutKeyRef.current = '';

						const layoutResult = applyLayout(currentNodes, latestEdgesRef.current, { useContainerLayoutV2 });
						const moved = didLayoutMoveNodes(currentNodes, layoutResult.nodes);
						lastLayoutRunAtRef.current = Date.now();
						layoutPassCountRef.current += 1;
						return moved ? layoutResult.nodes : currentNodes;
					});

					// Release the guard after React has had time to process the
					// layout-triggered dimension changes. Use double-RAF so both the
					// React commit and React Flow's internal measure pass complete
					// before we accept new dimension changes.
					requestAnimationFrame(() => {
						requestAnimationFrame(() => {
							isLayoutInProgressRef.current = false;

							// After the first layout completes, fit the viewport so the
							// user sees the entire flow centered.
							if (shouldFitAfterLayout) {
								requestAnimationFrame(() => {
									fitView(DEFAULT_CANVAS_FIT_VIEW_OPTIONS);
								});
							}

							// If dimension changes arrived while the guard was active
							// (e.g. a node finished collapsing), run another layout pass
							// with the now-accurate measurements.
							if (needsRelayoutRef.current) {
								needsRelayoutRef.current = false;
								if (layoutPassCountRef.current >= MAX_LAYOUT_PASSES_PER_BURST) {
									return;
								}
								if (pendingLayoutRef.current) {
									return;
								}
								pendingLayoutRef.current = setTimeout(() => {
									isLayoutInProgressRef.current = true;
									pendingAutoLayoutKeyRef.current = '';
									setNodes(currentNodes => {
										const key = buildAutoLayoutKey(currentNodes, latestEdgesRef.current);
										lastAutoLayoutKeyRef.current = key;

										const layoutResult = applyLayout(currentNodes, latestEdgesRef.current, {
											useContainerLayoutV2,
										});
										const moved = didLayoutMoveNodes(currentNodes, layoutResult.nodes);
										lastLayoutRunAtRef.current = Date.now();
										layoutPassCountRef.current += 1;
										return moved ? layoutResult.nodes : currentNodes;
									});
									requestAnimationFrame(() => {
										requestAnimationFrame(() => {
											isLayoutInProgressRef.current = false;
										});
									});
									pendingLayoutRef.current = null;
								}, 50); // Shorter debounce for follow-up — measurements have settled
							}
						});
					});
					pendingLayoutRef.current = null;
				}, 150); // 150ms debounce - lets node measurements settle before re-layout

				return updatedNodes;
			});
		},
		[fitView, hasContainerSize, setNodes, useContainerLayoutV2]
	);

	// Edge change handler
	const onEdgesChange = useCallback(
		(changes: EdgeChange[]) => {
			setEdges(eds => applyEdgeChanges(changes, eds));
		},
		[setEdges]
	);

	// Connection validation
	const canAddOutgoingEdge = useCallback(
		(sourceId: string, sourceHandleId?: string | null): boolean => {
			const sourceNode = nodes.find((n: Node<BaseNodeData>) => n.id === sourceId);
			if (!sourceNode) {
				return false;
			}
			if (sourceNode.type === 'trigger') {
				return true;
			}
			if (sourceNode.type === NODE_TYPES.TRIGGER_AUTOMATION_STEP) {
				return false;
			}
			const existingEdges = edges.filter((e: Edge) => e.source === sourceId && e.sourceHandle === sourceHandleId);
			return existingEdges.length === 0;
		},
		[nodes, edges]
	);

	// Connection handler
	const onConnect: OnConnect = useCallback(
		(connection: Connection) => {
			if (!connection.source || !connection.target) {
				return;
			}
			if (wouldCreateCycle(connection.source, connection.target, edges)) {
				toast.error('Cannot create a cycle. Automation flows must not loop back on themselves.');
				return;
			}
			if (!canAddOutgoingEdge(connection.source, connection.sourceHandle)) {
				toast.error('This connection point already has an outgoing connection. Remove it first to create a new one.');
				return;
			}
			takeSnapshot('edge_add', 'Add connection');
			setEdges((eds: Edge[]) =>
				addEdge({ ...connection, type: 'default', animated: animateEdges, data: { activeContacts } }, eds)
			);
		},
		[edges, setEdges, animateEdges, activeContacts, canAddOutgoingEdge, takeSnapshot]
	);

	// Node drag handlers for group drag
	const handleNodeDragStart: NodeMouseHandler<Node<BaseNodeData>> = useCallback(
		(event, node) => {
			const selectedNodes = nodes.filter(n => n.selected);
			const isPartOfSelection = selectedNodes.length > 1 && selectedNodes.some(n => n.id === node.id);
			if (!isPartOfSelection) {
				return;
			}
			const result = buildSelectionGroup(selectedNodes.map(n => n.id));
			if (result.success && result.group) {
				const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
				startGroupDrag(result.group, position);
			}
		},
		[nodes, buildSelectionGroup, screenToFlowPosition, startGroupDrag]
	);

	const handleNodeDrag: NodeMouseHandler<Node<BaseNodeData>> = useCallback(
		event => {
			if (groupDragState.isDragging) {
				const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
				updateGroupDrag(position);
			}
		},
		[groupDragState.isDragging, screenToFlowPosition, updateGroupDrag]
	);

	const handleNodeDragStop: NodeMouseHandler<Node<BaseNodeData>> = useCallback(() => {
		if (groupDragState.isDragging) {
			endGroupDrag();
		}
	}, [groupDragState.isDragging, endGroupDrag]);

	// Context menu handler
	const handleNodeContextMenu: NodeMouseHandler<Node<BaseNodeData>> = useCallback(
		(event, node) => {
			// React portals preserve React event bubbling even when the DOM is outside the canvas.
			// Fullscreen modals opened from a node (for example the email composer) should not reopen
			// the canvas context menu when users right-click inside the modal. Use the native DOM event
			// path instead of `instanceof Node`; admin composer/editor content can cross DOM realms.
			if (isContextMenuEventOutsideCanvas(event, containerRef.current)) {
				event.preventDefault();
				event.stopPropagation();
				closeContextMenu();
				return;
			}

			if (!modeConfig.isReadOnly) {
				openContextMenu(event, node);
			}
		},
		[modeConfig.isReadOnly, openContextMenu, closeContextMenu]
	);

	const handleOpenNodeMenu = useCallback(
		(nodeId: string, position: { x: number; y: number }) => {
			if (modeConfig.isReadOnly) {
				return;
			}

			const targetNode = nodes.find(node => node.id === nodeId);
			if (!targetNode) {
				return;
			}

			openContextMenuAtPosition(targetNode, position);
		},
		[modeConfig.isReadOnly, nodes, openContextMenuAtPosition]
	);

	const handleSuppressContextMenu = useCallback(
		(event: MouseEvent | ReactMouseEvent) => {
			event.preventDefault();
			event.stopPropagation();
			closeContextMenu();
		},
		[closeContextMenu]
	);

	const handleAddSegment = useCallback(
		async (switchNodeId: string) => {
			if (!onAddSegment) {
				return null;
			}

			const persistedSwitchStep = await onAddSegment(switchNodeId);
			if (!persistedSwitchStep || !Array.isArray(persistedSwitchStep.cases)) {
				return persistedSwitchStep;
			}

			const nextGraph = applyPersistedSwitchCaseGraphUpdate({
				nodes: latestNodesRef.current,
				edges: latestEdgesRef.current,
				switchNodeId,
				persistedSwitchStep,
			});

			setNodes(nextGraph.nodes);
			setEdges(nextGraph.edges);
			return persistedSwitchStep;
		},
		[onAddSegment, setNodes, setEdges]
	);

	// Open reorder segments modal for a switch node
	const handleOpenReorderSegments = useCallback(
		(switchNodeId: string) => {
			openReorderSegmentsModal(switchNodeId, nodes, edges);
		},
		[nodes, edges, openReorderSegmentsModal]
	);

	// Handle segment reordering from the modal
	const handleReorderSegments = useCallback(
		(newOrder: string[]) => {
			if (!reorderSegmentsModalState.parentSwitchNodeId) {
				return;
			}

			takeSnapshot('batch', 'Reorder segments');

			const localReorderResult = applyLocalSegmentOrder(
				reorderSegmentsModalState.parentSwitchNodeId,
				nodes,
				edges,
				newOrder
			);

			// Re-apply layout to update positions, including nested branch lanes that depend on branch edge caseIndex.
			const layoutResult = applyLayout(localReorderResult.nodes, localReorderResult.edges, { useContainerLayoutV2 });

			setNodes(layoutResult.nodes);
			setEdges(layoutResult.edges);

			// Persist the new order to the API.
			// Nested switches belong to child templates, so use the owner template from the
			// hidden switch node rather than always falling back to the root canvas template.
			if (onReorderSegments) {
				// Map segment node IDs to API case IDs
				const caseIds = newOrder
					.map(segmentNodeId => {
						const segNode = nodes.find(n => n.id === segmentNodeId);
						return segNode ? (segNode.data as SegmentNodeData).caseId : undefined;
					})
					.filter((id): id is string => id !== undefined);

				// Get the switch step's API stepId from the parent switch node
				const switchNode = nodes.find(n => n.id === reorderSegmentsModalState.parentSwitchNodeId);
				const stepId = switchNode ? (switchNode.data as AutomationStepNodeData).stepId : undefined;
				const ownerTemplateId = reorderSegmentsModalState.parentSwitchNodeId
					? resolveSwitchOwnerTemplateId(nodes, reorderSegmentsModalState.parentSwitchNodeId) || templateId
					: templateId;

				if (caseIds.length > 0 && stepId && ownerTemplateId) {
					onReorderSegments(caseIds, ownerTemplateId, stepId).catch(() => {
						toast.error('Failed to persist segment order');
					});
				}
			}
		},
		[
			nodes,
			edges,
			reorderSegmentsModalState.parentSwitchNodeId,
			takeSnapshot,
			setNodes,
			setEdges,
			onReorderSegments,
			templateId,
			useContainerLayoutV2,
		]
	);

	// Calculate if the current context menu node has enough segments to reorder
	// Works for both switch nodes and segment nodes (via parentSwitchNodeId)
	const canReorderSegments = useMemo(() => {
		// Determine the switch node ID - either the target (if switch) or parent (if segment)
		let switchNodeId: string | null = null;
		if (contextMenuState.isSwitchNode && contextMenuState.targetNodeId) {
			switchNodeId = contextMenuState.targetNodeId;
		} else if (contextMenuState.isSegmentNode && contextMenuState.parentSwitchNodeId) {
			switchNodeId = contextMenuState.parentSwitchNodeId;
		}

		if (!switchNodeId) {
			return false;
		}

		const orderedSegments = getOrderedSegments(switchNodeId, nodes, edges);
		// Need at least 2 non-default segments to reorder
		const nonDefaultCount = orderedSegments.filter(s => !s.data.isDefault).length;
		return nonDefaultCount >= 2;
	}, [
		contextMenuState.isSwitchNode,
		contextMenuState.isSegmentNode,
		contextMenuState.targetNodeId,
		contextMenuState.parentSwitchNodeId,
		nodes,
		edges,
	]);

	// Calculate if the current context menu step node can move up or down
	const { canMoveStepUpValue, canMoveStepDownValue } = useMemo(() => {
		if (!contextMenuState.targetNodeId) {
			return { canMoveStepUpValue: false, canMoveStepDownValue: false };
		}
		return {
			canMoveStepUpValue: canMoveStepUp(contextMenuState.targetNodeId, nodes, edges),
			canMoveStepDownValue: canMoveStepDown(contextMenuState.targetNodeId, nodes, edges),
		};
	}, [contextMenuState.targetNodeId, nodes, edges]);

	// Toolbar handlers
	const toggleAnimateEdges = useCallback(() => {
		setAnimateEdges(prev => {
			const newValue = !prev;
			setEdges((eds: Edge[]) => eds.map((edge: Edge) => ({ ...edge, animated: newValue })));
			return newValue;
		});
	}, [setEdges]);

	const toggleAutoConnect = useCallback(() => {
		setAutoConnect(prev => !prev);
	}, []);

	const handleSelectionChange: OnSelectionChangeFunc = useCallback(
		({ nodes: selectedNodes }) => {
			if (onSelectionChangeProp) {
				onSelectionChangeProp(selectedNodes.map(n => n.id));
			}
		},
		[onSelectionChangeProp]
	);

	// Sync nodes and edges to parent in a single RAF callback.
	// Combining them prevents races where the parent sees new nodes with stale edges.
	// IMPORTANT: Only sync back to parent AFTER the canvas has processed its initial
	// nodes (lastSyncedIdsRef is non-empty). Without this guard, the canvas can
	// overwrite the parent's nodes with [] before it has processed the initialNodes,
	// causing a blank canvas on mount.
	useEffect(() => {
		if (!onNodesChangeProp && !onEdgesChangeProp) {
			return;
		}
		if (!lastSyncedIdsRef.current) {
			return;
		}
		if (pendingParentSyncRafRef.current !== null) {
			return;
		}

		pendingParentSyncRafRef.current = requestAnimationFrame(() => {
			pendingParentSyncRafRef.current = null;
			if (onNodesChangeProp) {
				onNodesChangeProp(latestNodesRef.current);
			}
			if (onEdgesChangeProp) {
				onEdgesChangeProp(latestEdgesRef.current);
			}
		});
	}, [nodes, edges, onNodesChangeProp, onEdgesChangeProp]);

	useEffect(() => {
		const activeContactsByEdgeId = new Map<string, FlowContact[]>();
		const activeContactsByEdgeKey = new Map<string, FlowContact[]>();

		const toEdgeKey = (edge: Edge) =>
			`${edge.source}|${edge.target}|${edge.type ?? ''}|${edge.sourceHandle ?? ''}|${edge.targetHandle ?? ''}`;

		for (const edge of initialEdges) {
			const edgeData = edge.data as { activeContacts?: FlowContact[] } | undefined;
			const contactsForEdge = edgeData?.activeContacts ?? [];
			activeContactsByEdgeId.set(edge.id, contactsForEdge);
			activeContactsByEdgeKey.set(toEdgeKey(edge), contactsForEdge);
		}

		setEdges((currentEdges: Edge[]) => {
			let changed = false;
			const nextEdges = currentEdges.map((edge: Edge) => {
				const contactsById = activeContactsByEdgeId.get(edge.id);
				const contactsByKey = activeContactsByEdgeKey.get(toEdgeKey(edge));
				const nextActiveContacts = activeContacts.length === 0 ? [] : (contactsById ?? contactsByKey ?? []);
				const previousActiveContacts = (edge.data as { activeContacts?: FlowContact[] } | undefined)?.activeContacts;

				if (areContactsEqualById(previousActiveContacts, nextActiveContacts)) {
					return edge;
				}

				changed = true;
				return {
					...edge,
					data: {
						...edge.data,
						activeContacts: nextActiveContacts,
					},
				};
			});

			return changed ? nextEdges : currentEdges;
		});
	}, [initialEdges, activeContacts, setEdges]);

	const canvasNodes = useMemo(() => nodes.map(node => normalizeBranchEntryNodeInteractivity(node)), [nodes]);

	// Handlers are always provided so React Flow can report internal updates (notably `dimensions`)
	// even in read-only / preview mode. Interactivity is controlled via the ReactFlow props below
	// (nodesDraggable/nodesConnectable/elementsSelectable/etc.).
	const nodeChangeHandler = onNodesChange;
	const edgeChangeHandler = onEdgesChange;
	const connectHandler = modeConfig.nodesConnectable ? onConnect : undefined;
	const selectionChangeHandler = modeConfig.elementsSelectable ? handleSelectionChange : undefined;

	return (
		<AutomationFlowProvider
			onAddPath={handleAddSegment}
			updateNodeData={updateNodeData}
			onUpdateCaseCriteria={onUpdateCaseCriteria}
			onUpdateCaseStageIds={onUpdateCaseStageIds}
			onUpdateCaseName={onUpdateCaseName}
			onOpenNodeMenu={handleOpenNodeMenu}
			showAddPath={!modeConfig.isReadOnly}
			isReadOnly={modeConfig.isReadOnly}
			automationTemplate={automationTemplate}
			connectionTypes={connectionTypes}
			impersonationContext={impersonationContext}
		>
			<div ref={containerRef} className={css(styleSheet.container)}>
				{hasContainerSize && (
					<ReactFlow
						onInit={handleCanvasInit}
						style={{ width: '100%', height: '100%', minHeight: 650 }}
						nodes={canvasNodes}
						edges={edges}
						onNodesChange={nodeChangeHandler}
						onEdgesChange={edgeChangeHandler}
						onConnect={connectHandler}
						onSelectionChange={selectionChangeHandler}
						onDragOver={modeConfig.isReadOnly ? undefined : onDragOver}
						onDragLeave={modeConfig.isReadOnly ? undefined : onDragLeave}
						onDrop={modeConfig.isReadOnly ? undefined : onDrop}
						onNodeContextMenu={
							modeConfig.isReadOnly
								? (handleSuppressContextMenu as NodeMouseHandler<Node<BaseNodeData>>)
								: handleNodeContextMenu
						}
						onPaneContextMenu={modeConfig.isReadOnly ? handleSuppressContextMenu : undefined}
						onNodeDragStart={modeConfig.nodesDraggable ? handleNodeDragStart : undefined}
						onNodeDrag={modeConfig.nodesDraggable ? handleNodeDrag : undefined}
						onNodeDragStop={modeConfig.nodesDraggable ? handleNodeDragStop : undefined}
						onPaneClick={closeContextMenu}
						nodeTypes={nodeTypes}
						edgeTypes={edgeTypes}
						panOnScroll
						panOnDrag={modeConfig.panOnDrag}
						selectionOnDrag={modeConfig.selectionOnDrag}
						multiSelectionKeyCode={modeConfig.multiSelectionKeyCode}
						nodesDraggable={modeConfig.nodesDraggable}
						nodesConnectable={modeConfig.nodesConnectable}
						elementsSelectable={modeConfig.elementsSelectable}
						proOptions={{ hideAttribution: true }}
						minZoom={0.3}
						maxZoom={3}
						deleteKeyCode={null}
						nodesFocusable={modeConfig.elementsSelectable}
						edgesFocusable={false}
						aria-label='Automation workflow canvas'
					>
						{isDebugCanvas && (
							<Panel position='bottom-left' style={{ marginLeft: 8, marginBottom: 80, pointerEvents: 'none' }}>
								<div
									style={{
										background: 'rgba(15, 23, 42, 0.72)',
										color: '#fff',
										fontSize: 12,
										padding: '6px 8px',
										borderRadius: 6,
										fontFamily: 'monospace',
									}}
								>
									nodes: {nodes.length} | edges: {edges.length} | mode: {mode}
								</div>
							</Panel>
						)}
						<Background gap={32} color='#e2e8f0' />
						{isMiniMapVisible && (
							<MiniMap
								nodeColor={(node: Node) => {
									// Trigger nodes get their own color
									if (node.type === 'trigger') {
										return TRIGGER_INDICATOR_COLOR;
									}
									// Step nodes get color based on step type
									const stepData = node.data as AutomationStepNodeData | undefined;
									if (stepData?.stepType) {
										return getStepTypeBorderColor(stepData.stepType);
									}
									// Default color for other nodes
									return '#6366f1';
								}}
								nodeStrokeColor={() => '#374151'}
								maskColor='rgba(99, 102, 241, 0.1)'
								pannable
								zoomable
							/>
						)}
						<Controls position='bottom-left' showInteractive={false} />
						<NodeSearchPanel
							state={searchState}
							onSearch={updateSearchQuery}
							onNavigate={navigateToNode}
							onClose={closeSearch}
						/>
						<SelectionGroupOverlay dragState={groupDragState} />
						{!modeConfig.isReadOnly && (
							<FloatingAddSegmentButton
								switchNodeId={selectedSwitchNodeId ?? null}
								templateId={templateId}
								onAddSegment={handleAddSegment}
							/>
						)}
						{shouldShowChainTerminusButtons && (
							<ChainTerminusButtonsLayer
								termini={chainTermini}
								onAddStep={handleAddStepToChain}
								highlightedTerminusId={closestTerminusId}
								excludedStepTypes={effectiveExcludedStepTypes}
							/>
						)}
						{modeConfig.showToolbar && (
							<Panel position='top-right'>
								<CanvasControls
									animateEdges={animateEdges}
									onToggleAnimateEdges={toggleAnimateEdges}
									autoConnect={autoConnect}
									onToggleAutoConnect={toggleAutoConnect}
									onShowAllShortcuts={openHelp}
									isMiniMapVisible={isMiniMapVisible}
									onToggleMiniMap={toggleMiniMap}
									onRunAutoLayout={handleRunAutoLayout}
								/>
							</Panel>
						)}
					</ReactFlow>
				)}
				{modeConfig.showKeyboardShortcuts && <KeyboardShortcutsHelp isOpen={isHelpOpen} onClose={closeHelp} />}
				<CanvasContextMenu
					state={contextMenuState}
					onClose={closeContextMenu}
					onDelete={handleDelete}
					onDuplicate={handleDuplicate}
					onOpenReorderSegments={handleOpenReorderSegments}
					canReorderSegments={canReorderSegments}
					onMoveUp={handleMoveUp}
					onMoveDown={handleMoveDown}
					canMoveUp={canMoveStepUpValue}
					canMoveDown={canMoveStepDownValue}
				/>
				<DeleteStepConfirmationDialog
					isOpen={pendingDelete !== null}
					onClose={cancelDelete}
					onConfirm={confirmDelete}
					count={pendingDelete?.count ?? 0}
					primaryLabel={pendingDelete?.primaryLabel}
				/>
				<ReorderSegmentsModal
					isOpen={reorderSegmentsModalState.isOpen}
					onClose={closeReorderSegmentsModal}
					segments={reorderSegmentsModalState.segments}
					onReorder={handleReorderSegments}
				/>
			</div>
		</AutomationFlowProvider>
	);
}

/**
 * Layer component that renders chain terminus buttons with viewport transformations.
 */
interface ChainTerminusButtonsLayerProps {
	termini: ChainTerminus[];
	onAddStep: (terminus: ChainTerminus, stepType: string) => Promise<void>;
	highlightedTerminusId: string | null;
	excludedStepTypes?: string[];
}

function ChainTerminusButtonsLayer({
	termini,
	onAddStep,
	highlightedTerminusId,
	excludedStepTypes,
}: ChainTerminusButtonsLayerProps): React.ReactElement | null {
	const { x, y, zoom } = useViewport();

	if (termini.length === 0) {
		return null;
	}

	return (
		<Panel position='top-left' style={{ margin: 0, pointerEvents: 'none' }}>
			<div
				style={{
					transform: `translate(${x}px, ${y}px) scale(${zoom})`,
					transformOrigin: '0 0',
					position: 'absolute',
					top: 0,
					left: 0,
				}}
			>
				{termini.map(terminus => (
					<ChainTerminusButton
						key={terminus.nodeId}
						terminus={terminus}
						onAddStep={onAddStep}
						isHighlighted={terminus.nodeId === highlightedTerminusId}
						excludedStepTypes={excludedStepTypes}
					/>
				))}
			</div>
		</Panel>
	);
}
